import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { generatePath, useNavigate } from 'react-router-dom';
import { useDebounce } from 'react-use';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import LoadingButton from '@sorare/core/src/atoms/buttons/LoadingButton';
import { TextInput } from '@sorare/core/src/atoms/inputs/TextInput';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import Snackbar from '@sorare/core/src/atoms/snackbar/Snackbar';
import { BodyM, HeadlineL } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { FOOTBALL_PLAY_RIVALS_LEAGUES_SLUG_ONBOARDING } from '@sorare/core/src/constants/__generated__/routes';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useEvents from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';

import { LayeredGroupOverview } from 'components/rivalsUserGroup/LayeredGroupOverview';

import {
  AffiliatesLeaguesQuery,
  AffiliatesLeaguesQueryVariables,
  DialogLeagueQuery,
  DialogLeagueQueryVariables,
  JoinLeagueMutation,
  JoinLeagueMutationVariables,
} from './__generated__/index.graphql';

const BodyWrapper = styled(Vertical)`
  padding: var(--double-unit);
`;
const FooterWrapper = styled(Horizontal)`
  padding: var(--double-unit);
  & > * {
    flex: 1;
  }
`;
const SlidingContent = styled(Horizontal)<{ length: number }>`
  width: ${props => `calc(${props.length} * 100%)`};
  & > * {
    width: 100%;
  }
  transition: transform 0.3s ease;
`;

const Selector = styled(Horizontal)`
  align-self: center;
`;

const LeagueSelectorDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--c-neutral-400);
  &.selected {
    background: var(--c-neutral-700);
  }
`;

const TextInputWrapper = styled.div`
  height: 40px;
`;

export const JOIN_LEAGUE_MUTATION = gql`
  mutation JoinLeagueMutation($input: joinSo5UserGroupInput!) {
    joinSo5UserGroup(input: $input) {
      userGroup {
        slug
        ... on RivalsUserGroup {
          slug
          affiliate
        }
        myMembership {
          id
          createdAt
        }
        logo {
          id
          pictureUrl
        }
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<JoinLeagueMutation, JoinLeagueMutationVariables>;

// see packages/core/src/contexts/graphql/Provider.tsx's SILENCE_GQL_ERRORS_FROM_OPERATION_NAMES
const DIALOG_LEAGUE_QUERY = gql`
  query DialogLeagueQuery($joinSecret: String!) {
    football {
      rivals {
        id
        userGroup(joinSecret: $joinSecret) {
          slug
          affiliate
          ...LayeredGroupOverview_rivalsUserGroup
        }
      }
    }
  }
  ${LayeredGroupOverview.fragments.rivalsUserGroup}
` as TypedDocumentNode<DialogLeagueQuery, DialogLeagueQueryVariables>;

type Props = {
  open: boolean;
  onClose: () => void;
};

const messages = defineMessages({
  title: {
    id: 'JoinLeagueDialog.title2',
    defaultMessage: 'Join a league',
  },
  subtitle: {
    id: 'JoinLeagueDialog.title',
    defaultMessage: 'Do you have a friend code?',
  },
});

const AFFILIATES_LEAGUES_QUERY = gql`
  query AffiliatesLeaguesQuery {
    football {
      rivals {
        id
        userGroupTournament {
          id
          affiliateUserGroups(first: 100, featured: true) {
            nodes {
              slug
              joinSecret
              affiliate
              ...LayeredGroupOverview_rivalsUserGroup
            }
          }
        }
      }
    }
  }
  ${LayeredGroupOverview.fragments.rivalsUserGroup}
` as TypedDocumentNode<AffiliatesLeaguesQuery, AffiliatesLeaguesQueryVariables>;

export const JoinLeagueDialog = ({ open, onClose }: Props) => {
  const [join, { loading: mutationLoading }] = useMutation(
    JOIN_LEAGUE_MUTATION,
    {
      showErrorsWithSnackNotification: true,
      refetchQueries: ['LeaguesQuery'],
    }
  );

  const [joinSecretInput, setJoinSecretInput] = useState('');
  const [debouncedSecretInput, setDebouncedSecretInput] = useState('');
  const [selectedLeagueIndex, setSelectedLeagueIndex] = useState(0);

  const navigate = useNavigate();

  const finalOnClose = () => {
    setJoinSecretInput('');
    setSelectedLeagueIndex(0);
    onClose();
  };
  const [joinedUserGroupName, setjoinedUserGroupName] = useState<string>();

  useDebounce(
    () => {
      setDebouncedSecretInput(joinSecretInput);
    },
    1000,
    [joinSecretInput]
  );
  const isInputDebouncing = debouncedSecretInput !== joinSecretInput;

  const { data: affiliatesData, loading: affiliatesLoading } = useQuery(
    AFFILIATES_LEAGUES_QUERY,
    {
      skip: !open,
    }
  );
  const affiliatesUserGroups =
    affiliatesData?.football.rivals.userGroupTournament?.affiliateUserGroups
      .nodes;
  const leaguesLength = (affiliatesUserGroups?.length || 0) + 1;

  const selectedLeagueJoinSecret =
    selectedLeagueIndex === 0
      ? debouncedSecretInput
      : affiliatesUserGroups?.[selectedLeagueIndex - 1]?.joinSecret;

  const { data, loading } = useQuery(DIALOG_LEAGUE_QUERY, {
    variables: {
      joinSecret: debouncedSecretInput,
    },
    skip: debouncedSecretInput?.length !== 6,
  });

  const track = useEvents();

  const userGroupFromInput = data?.football.rivals.userGroup;
  const selectedUserGroup =
    selectedLeagueIndex === 0
      ? data?.football.rivals.userGroup
      : affiliatesUserGroups?.[selectedLeagueIndex - 1];
  return (
    <>
      <Dialog
        maxWidth="xs"
        fullWidth
        open={open}
        onClose={finalOnClose}
        title={
          <Vertical gap={0} center>
            <HeadlineL className="text-center">
              <FormattedMessage {...messages.title} />
            </HeadlineL>
            <BodyM color="var(--c-neutral-600)" className="text-center">
              <FormattedMessage {...messages.subtitle} />
            </BodyM>
          </Vertical>
        }
        footer={
          <FooterWrapper>
            <Button size="medium" onClick={finalOnClose} color="tertiary">
              <FormattedMessage {...glossary.cancel} />
            </Button>
            <LoadingButton
              disabled={!selectedLeagueJoinSecret}
              size="medium"
              loading={
                mutationLoading ||
                (selectedLeagueIndex === 0 && isInputDebouncing)
              }
              onClick={async () => {
                try {
                  const { errors } = await join({
                    variables: {
                      input: {
                        joinSecret: selectedLeagueJoinSecret!,
                      },
                    },
                  });
                  if (errors?.length) {
                    return;
                  }
                  if (!selectedUserGroup?.affiliate) {
                    navigate(
                      generatePath(
                        FOOTBALL_PLAY_RIVALS_LEAGUES_SLUG_ONBOARDING,
                        {
                          slug: selectedUserGroup!.slug,
                        }
                      )
                    );
                  } else {
                    setjoinedUserGroupName(selectedUserGroup?.displayName);
                  }
                } catch {
                  return;
                } finally {
                  finalOnClose();
                }
                track('Submit Join Rivals League', {
                  leagueSlug: selectedUserGroup!.slug,
                });
              }}
              color="primary"
            >
              <FormattedMessage {...glossary.join} />
            </LoadingButton>
          </FooterWrapper>
        }
      >
        <BodyWrapper gap={2}>
          <TextInputWrapper>
            {selectedLeagueIndex === 0 && (
              <TextInput
                value={joinSecretInput}
                placeholder="Enter code"
                onChange={e => {
                  setJoinSecretInput(e.currentTarget.value.trim());
                }}
              />
            )}
          </TextInputWrapper>
          <SlidingContent
            style={{
              transform: `translateX(calc(-1/${leaguesLength} * ${selectedLeagueIndex} * 100%)`,
            }}
            length={leaguesLength}
          >
            <LayeredGroupOverview
              userGroup={userGroupFromInput}
              loading={loading || isInputDebouncing}
            >
              {!joinSecretInput && !affiliatesLoading && leaguesLength > 1 && (
                <ButtonBase
                  onClick={() => {
                    setSelectedLeagueIndex(1);
                  }}
                >
                  <BodyM color="var(--c-brand-300)" bold>
                    <FormattedMessage
                      id="JoinLeagueDialog.IhaveNoCode"
                      defaultMessage="I have no code"
                    />
                  </BodyM>
                </ButtonBase>
              )}
            </LayeredGroupOverview>
            {affiliatesUserGroups?.map((affiliateGroup, index) => (
              <ButtonBase
                key={affiliateGroup.slug}
                onClick={() => {
                  setSelectedLeagueIndex(index + 1);
                }}
              >
                <LayeredGroupOverview userGroup={affiliateGroup} />
              </ButtonBase>
            ))}
          </SlidingContent>
          {leaguesLength > 1 && (
            <Skeleton loading={affiliatesLoading}>
              <Selector gap={0.5}>
                {Array.from({ length: leaguesLength }).map((_, index) => (
                  <ButtonBase
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    onClick={() => {
                      setSelectedLeagueIndex(index);
                    }}
                  >
                    <LeagueSelectorDot
                      className={classNames({
                        selected: index === selectedLeagueIndex,
                      })}
                    />
                  </ButtonBase>
                ))}
              </Selector>
            </Skeleton>
          )}
        </BodyWrapper>
      </Dialog>
      <Snackbar
        open={!!joinedUserGroupName}
        message={
          <FormattedMessage
            id="JoinLeagueDialog.joinSuccess"
            defaultMessage="You have successfully joined the league {name}"
            values={{ name: joinedUserGroupName }}
          />
        }
        onClose={() => {
          setjoinedUserGroupName(undefined);
        }}
      />
    </>
  );
};
